import axios from 'axios';
import authService from './authService';

const API_URL = '/api/surveys/';

// Add a request interceptor to include the auth token for admin endpoints
axios.interceptors.request.use(
  (config) => {
    const user = authService.getCurrentUser();
    if (user && user.accessToken && config.url.includes('/admin/')) {
      config.headers.Authorization = 'Bearer ' + user.accessToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class SurveyService {
  // Public endpoints (no authentication required)
  
  getActiveSurveys() {
    return axios.get(API_URL + 'public');
  }

  getSurveyById(id) {
    return axios.get(API_URL + `public/${id}`);
  }

  getSurveyQuestions(id, page = 0) {
    return axios.get(API_URL + `public/${id}/questions?page=${page}`);
  }

  startSurvey(id) {
    return axios.post(API_URL + `public/${id}/start`);
  }

  saveAnswers(responseId, answers) {
    return axios.post(API_URL + `public/responses/${responseId}/answers`, answers);
  }

  completeSurvey(responseId) {
    return axios.post(API_URL + `public/responses/${responseId}/complete`);
  }

  getSurveyResults(id) {
    return axios.get(API_URL + `public/${id}/results`);
  }

  // Admin endpoints (require ADMIN role)
  
  getAllSurveys(page = 0, size = 10, sortBy = 'id', sortDir = 'desc') {
    return axios.get(API_URL + `admin?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
  }

  getSurveyByIdForAdmin(id) {
    return axios.get(API_URL + `admin/${id}`);
  }

  createSurvey(surveyData) {
    return axios.post(API_URL + 'admin', surveyData);
  }

  updateSurvey(id, surveyData) {
    return axios.put(API_URL + `admin/${id}`, surveyData);
  }

  deleteSurvey(id) {
    return axios.delete(API_URL + `admin/${id}`);
  }

  getSurveyStatistics(id) {
    return axios.get(API_URL + `admin/${id}/statistics`);
  }

  // Utility methods for form handling
  
  createAnswerRequest(questionId, questionType, value, selectedOptions = []) {
    const answer = {
      questionId: questionId
    };

    switch (questionType) {
      case 'TEXT_FIELD':
      case 'TEXT_AREA':
      case 'EMAIL':
        answer.answerText = value;
        break;
      case 'NUMBER':
      case 'RATING':
        answer.answerText = value ? value.toString() : null;
        break;
      case 'DATE':
        answer.answerDate = value ? new Date(value).toISOString() : null;
        break;
      case 'SINGLE_CHOICE':
      case 'DROPDOWN':
      case 'RADIO':
      case 'YES_NO':
        answer.selectedOptionId = value;
        break;
      case 'MULTIPLE_CHOICE':
      case 'CHECKBOX':
        answer.selectedOptionIds = selectedOptions;
        break;
      default:
        answer.answerText = value;
    }

    return answer;
  }

  validateAnswer(question, value, selectedOptions = []) {
    if (question.required) {
      switch (question.questionType) {
        case 'TEXT_FIELD':
        case 'TEXT_AREA':
        case 'EMAIL':
          return value && value.trim().length > 0;
        case 'NUMBER':
        case 'RATING':
          return value !== null && value !== undefined && value !== '';
        case 'DATE':
          return value !== null && value !== undefined;
        case 'SINGLE_CHOICE':
        case 'DROPDOWN':
        case 'RADIO':
        case 'YES_NO':
          return value !== null && value !== undefined;
        case 'MULTIPLE_CHOICE':
        case 'CHECKBOX':
          return selectedOptions && selectedOptions.length > 0;
        default:
          return true;
      }
    }
    return true;
  }

  formatAnswerForDisplay(answer) {
    switch (answer.questionType) {
      case 'TEXT_FIELD':
      case 'TEXT_AREA':
      case 'EMAIL':
        return answer.answerText || 'No answer';
      case 'NUMBER':
      case 'RATING':
        return answer.answerNumber !== null ? answer.answerNumber.toString() : 'No answer';
      case 'DATE':
        return answer.answerDate ? new Date(answer.answerDate).toLocaleDateString() : 'No answer';
      case 'SINGLE_CHOICE':
      case 'DROPDOWN':
      case 'RADIO':
      case 'YES_NO':
        return answer.selectedOptionText || 'No selection';
      case 'MULTIPLE_CHOICE':
      case 'CHECKBOX':
        return answer.selectedOptionText || 'No selections';
      default:
        return answer.answerText || 'No answer';
    }
  }
}

export default new SurveyService();